import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Inbox, Lock } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka } from '@/utils/number';
import { PcmpHoldActionButtons } from '../PcmpHoldActions/PcmpHoldActionButtons';
import {
  pcmpCorrectionCountsQueryOptions,
  pcmpCorrectionQueueQueryOptions,
} from '../../api/pcmpCorrection.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { usePcmpCorrectionFilters } from '../../hooks/usePcmpCorrectionFilters';
import type { PcmpCorrectionQueueItem } from '../../types/pcmpCorrection.types';
import { PcmpFailureSummary } from '../PcmpAccountsQueue/PcmpAccountsCells';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { getPcmpQueueColumns } from '../PcmpQueue/pcmpQueueColumns';
import { PcmpCorrectionActionButtons } from './PcmpCorrectionActionButtons';

const paymentColumn: ColumnDef<PcmpCorrectionQueueItem> = {
  id: 'payment',
  header: 'Payment',
  cell: ({ row }) => <span className="whitespace-nowrap">{row.original.paymentTypeLabel}</span>,
};

const failureColumn: ColumnDef<PcmpCorrectionQueueItem> = {
  id: 'failure',
  header: 'Failure',
  cell: ({ row }) => {
    const { failure } = row.original;
    if (!failure) return <PcmpEmptyCell />;
    return (
      <div className="flex flex-col gap-0.5">
        {failure.batchNo !== null ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            Batch #{failure.batchNo}
          </span>
        ) : null}
        <PcmpFailureSummary
          stageLabel={failure.failureStageLabel}
          reason={failure.failureReason}
          by={failure.failedBy}
          at={failure.failedAt}
        />
      </div>
    );
  },
};

function renderRowActions(item: PcmpCorrectionQueueItem) {
  return (
    <>
      {item.canCorrect ? (
        <PcmpCorrectionActionButtons
          proposalId={item.proposalId}
          paymentType={item.paymentType}
          failure={item.failure}
          className="flex-nowrap"
        />
      ) : item.correctionBlockedReason ? (
        <span
          className="flex items-center text-muted-foreground"
          title={item.correctionBlockedReason}
        >
          <Lock className="size-3.5" aria-hidden />
          <span className="sr-only">{item.correctionBlockedReason}</span>
        </span>
      ) : null}
      <PcmpHoldActionButtons
        proposalId={item.proposalId}
        refNo={item.refNo}
        canHold={item.canHold}
        canRelease={item.canRelease}
        hold={item.hold}
        className="flex-nowrap"
      />
      <Link
        to="/pcmp/proposals/$proposalId"
        params={{ proposalId: item.proposalId }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
      >
        <Eye className="size-3.5" />
        View
      </Link>
    </>
  );
}

/**
 * Legacy CorrectionQueue: failed Accounts payments the proposer's chain (or an
 * admin) corrects and approves for re-disbursement.
 */
export function PcmpCorrectionQueueTable() {
  const router = useRouter();
  const controller = usePcmpCorrectionFilters();
  const { page, limit, appliedFilters, queueParams } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpCorrectionQueueQueryOptions(queueParams),
  );
  const { data: counts } = useQuery(pcmpCorrectionCountsQueryOptions(appliedFilters));

  useKeepPageInRange(page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpCorrectionQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const columns = useMemo(
    () =>
      getPcmpQueueColumns<PcmpCorrectionQueueItem>({
        afterAmounts: [paymentColumn],
        afterLanes: [failureColumn],
        renderActions: renderRowActions,
      }),
    [],
  );

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Payment Correction</h1>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {counts
            ? `${formatNumber(counts.correction)} awaiting correction · ${formatTaka(
                counts.correctionAmount,
              )}`
            : ' '}
        </p>
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-correction" />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the correction queue"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={data?.data ?? []}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage="No failed payments await correction"
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) => (item.canCorrect ? 'bg-primary/5' : undefined)}
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </div>
  );
}
